import { useEffect, useRef, useState } from "react";
import { Camera, ScanLine, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const cameraSupported = () => typeof window !== "undefined" && "BarcodeDetector" in window;

/** Barcode entry: a keyboard-wedge scanner types into the box, or use the device camera. */
export const BarcodeScanField = ({ onResolved, testid = "barcode", module = "samples" }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const stopRef = useRef(null);

  const resolve = async (value) => {
    const scanned = String(value || "").trim();
    if (!scanned) return;
    setBusy(true);
    try {
      const { data } = await api.get("/lookup/barcode", { params: { code: scanned, module } });
      setLastScan(data);
      onResolved?.(data);
      toast.success(data.found
        ? `Found in ${data.source} — details filled in`
        : "New barcode — please enter the details once");
      setCode("");
      inputRef.current?.focus();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => () => stopRef.current?.(), []);

  const startCamera = async () => {
    if (!cameraSupported()) {
      toast.error("This browser cannot scan with the camera — use the attached scanner or type the code");
      return;
    }
    setCamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const detector = new window.BarcodeDetector();
      let live = true;
      stopRef.current = () => {
        live = false;
        stream.getTracks().forEach((t) => t.stop());
        stopRef.current = null;
      };
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const tick = async () => {
        if (!live || !videoRef.current) return;
        try {
          const found = await detector.detect(videoRef.current);
          if (found?.length) {
            stopRef.current?.();
            setCamOpen(false);
            await resolve(found[0].rawValue);
            return;
          }
        } catch (e) { console.warn("barcode detect frame failed:", e); }
        setTimeout(tick, 350);
      };
      tick();
    } catch (e) {
      setCamOpen(false);
      toast.error("Could not open the camera. Check permissions or use the attached scanner.");
      console.warn("camera scan failed:", e);
    }
  };

  const closeCamera = () => { stopRef.current?.(); setCamOpen(false); };

  return (
    <div className="rounded-md border border-[#002FA7]/30 bg-[#F5F8FF] p-3" data-testid={`${testid}-field`}>
      <div className="flex items-center gap-1.5">
        <ScanLine className="h-4 w-4 text-[#002FA7]" />
        <p className="text-[10px] uppercase tracking-[0.1em] text-[#002FA7] font-medium">Scan barcode</p>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Input ref={inputRef} autoFocus value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolve(code); } }}
          placeholder="Scan or type the sample barcode, then press Enter"
          className="h-10 flex-1 min-w-[200px] bg-white" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" className="h-10" onClick={() => resolve(code)}
          disabled={busy || !code.trim()} data-testid={`${testid}-lookup-btn`}>Look up</Button>
        <Button type="button" variant="outline" className="h-10" onClick={startCamera}
          data-testid={`${testid}-camera-btn`}><Camera className="h-4 w-4 mr-1.5" /> Camera</Button>
      </div>
      <p className="text-[11px] text-[#6B7280] mt-1.5">
        A keyboard-wedge scanner types straight into this box — no setup needed.
      </p>
      {lastScan && (
        <p className="text-xs mt-1.5" data-testid={`${testid}-result`}>
          {lastScan.found
            ? <span className="text-[#2E7D32]">Matched {lastScan.code} from {lastScan.source}</span>
            : <span className="text-[#B58900]">{lastScan.code} is new — details you enter will be reused</span>}
        </p>
      )}

      {camOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          data-testid={`${testid}-camera-overlay`} onClick={closeCamera}>
          <div className="bg-white rounded-lg p-3 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-heading font-bold text-sm">Point the camera at the barcode</p>
              <button onClick={closeCamera} data-testid={`${testid}-camera-close`}
                className="p-1.5 rounded hover:bg-[#F3F4F6]"><X className="h-4 w-4" /></button>
            </div>
            <video ref={videoRef} muted playsInline className="w-full rounded bg-black aspect-video" />
          </div>
        </div>
      )}
    </div>
  );
};
